import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { IAuthContext } from '../interfaces/IAuthContext'
import { initialAuthContext } from '../helpers/ctxHelper'
import { api, logoutEventName } from '../helpers/apiHelper'
import { useMsal } from '@azure/msal-react'

export const AuthContext = createContext<IAuthContext>(initialAuthContext)
type AuthContextProviderProps = {
   children: JSX.Element | JSX.Element[]
}
export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
   const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false)
   const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true)
   const [loginType, setLoginType] = useState<'bankid' | 'msal' | null>(localStorage.getItem('login_type') as 'bankid' | 'msal' | null)
   const timeoutRef = useRef<NodeJS.Timeout | null>(null)
   const { instance } = useMsal()

   useEffect(() => {
      const handleLogoutEvent = () => {
         logout(loginType)
      }
      window.addEventListener(logoutEventName, handleLogoutEvent)

      return () => {
         window.removeEventListener(logoutEventName, handleLogoutEvent)
      }
   }, [loginType])
   const checkAuthStatus = async () => {
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
            method: 'POST',
            credentials: 'include',
         })

         if (response.status === 200) {
            setIsLoggedIn(true)
         } else {
            setIsLoggedIn(false)
         }
      } catch (error) {
         setIsLoggedIn(false)
      } finally {
         setIsLoadingAuth(false)
      }
   }
   const setTimeOut = () => {
      const inactivityTime = 60 * 60 * 1000 // 60 minuter
      timeoutRef.current = setTimeout(() => {
         logout(loginType)
      }, inactivityTime)
   }
   const resetTimer = () => {
      if (isLoggedIn) {
         clearTimeout(timeoutRef.current)
         setTimeOut()
      }
   }
   useEffect(() => {
      const events: (keyof WindowEventMap)[] = ['load', 'mousemove', 'keydown', 'scroll', 'click']
      events.forEach((event) => window.addEventListener(event, resetTimer))

      return () => {
         events.forEach((event) => window.removeEventListener(event, resetTimer))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isLoggedIn])

   const startLogoutTimer = () => {
      clearTimeout(timeoutRef.current)

      setTimeOut()
      // 60 minutes in ms
   }
   useEffect(() => {
      checkAuthStatus()
      return () => {
         clearTimeout(timeoutRef.current)
      }
   }, [])
   useEffect(() => {}, [])

   const login = (type: 'bankid' | 'msal'): void => {
      localStorage.setItem('login_type', type)
      setLoginType(type)
      setIsLoggedIn(true)
      setIsLoadingAuth(false)
      startLogoutTimer()
   }

   const logout = async (loginType: 'bankid' | 'msal' | null) => {
      setIsLoggedIn(false)
      clearTimeout(timeoutRef.current)

      // MSAL logout for test environment clear session and local storage, then msal logout. Set manual_logout to prevent automatic login with msal in useEffect in Login.tsx
      if (loginType === 'msal') {

         localStorage.setItem('manual_logout', 'true')
      }
      
      api().revokeJwt()
   }
   return <AuthContext.Provider value={{ isLoggedIn, login, logout, isLoadingAuth, loginType }}>{children}</AuthContext.Provider>
}
export const useAuth = () => {
   const context = useContext(AuthContext)
   if (context === undefined) {
      throw new Error('useAuth must be used within an AuthProvider')
   }
   return context
}
